// The data of Shot Map (T2-6), Pass Map (T2-7), Heat Map (T2-8) and Passing Profile (T2-9) on
// ?live=1 — the same three things the snapshot gives them: her events tree, the competition + season it
// resolved to, and the match context. Asked step by step (live/event-map.ts): her competitions
// once, the shown competition's seasons, then the shown season's events + context. A
// competition or season switch asks only for what's new; the widget's own answers are kept
// (live/query.ts).

import { useMemo } from "react";
import type { ProtoEventCompetition, ProtoShotPassDetail } from "@catbento/shared";
import { getModState, setModState } from "../modules/bridge.js";
import type { EventMapData } from "../modules/EventMapBody.js";
import type { EventContext } from "../engines/event-map-kit.js";
import type { PlayerId } from "../data/players.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import {
  loadEventCompetitions,
  loadEventSeason,
  loadEventSeasons,
  resolveEventCompetition,
  resolveEventSeason,
  storedForPlayer,
  type EventKind,
} from "../live/event-map.js";

const NO_CONTEXT: EventContext = { matchResults: {}, matchGoals: {}, playersById: new Map() };

/** `kinds`: the events to load — one of `EVENT_KINDS`. */
export function useLiveEventMap(mod: HTMLElement, playerId: PlayerId | null, kinds: readonly EventKind[]): EventMapData {
  // Each widget keeps only its own answers (live/query.ts), so this only names them.
  const kindsKey = kinds.join("+");
  const comps = useLiveQuery(playerId === null ? null : `${kindsKey}:comps:${playerId}`, () =>
    loadEventCompetitions(liveApi(), playerId as PlayerId),
  );
  // The stored league + season count only for the player they were stored for (a new player
  // opens on his own league, current season).
  const stored = storedForPlayer(getModState<PlayerId>(mod, "_eventMapPlayer"), playerId, {
    compId: getModState<string>(mod, "_eventMapCompetitionId"),
    seasonCode: getModState<string>(mod, "_eventMapSeason"),
  });
  const compId = comps ? resolveEventCompetition(comps, stored.compId) : null;
  const seasonCodes = useLiveQuery(compId === null ? null : `${kindsKey}:seasons:${playerId}:${compId}`, () =>
    loadEventSeasons(liveApi(), playerId as PlayerId, compId as number),
  );
  const seasonCode = seasonCodes ? resolveEventSeason(seasonCodes, stored.seasonCode) : null;
  const season = useLiveQuery(seasonCode === null ? null : `${kindsKey}:season:${playerId}:${compId}:${seasonCode}`, () =>
    loadEventSeason(liveApi(), kinds, playerId as PlayerId, compId as number, seasonCode as string),
  );

  // The resolved ids go back on the module, as the snapshot path does — Undo, presets and
  // "All" capture them, and the pickers show them.
  if (compId !== null && seasonCode !== null) {
    setModState(mod, "_eventMapCompetitionId", String(compId));
    setModState(mod, "_eventMapSeason", seasonCode);
    setModState(mod, "_eventMapPlayer", playerId);
  }

  return useMemo<EventMapData>(() => {
    if (!comps || compId === null || !seasonCodes || seasonCode === null || !season) {
      return { spd: null, state: null, ctx: NO_CONTEXT };
    }
    const compRef = comps.competitions.find((c) => c.id === compId)!;
    // The snapshot's tree, filled only where it's shown: every season of this competition is
    // listed (the season picker), but only the shown one has events, and only the kinds this
    // module asked for.
    const comp: ProtoEventCompetition = {
      name: compRef.name,
      seasons: Object.fromEntries(
        seasonCodes.map((code) =>
          [code, code === seasonCode ? { shots: season.shots, passes: season.passes } : { shots: [], passes: [] }],
        ),
      ),
    };
    const spd: ProtoShotPassDetail = {
      playerId: playerId as number,
      playerName: "",
      competitions: comps.competitions.map((c) => ({ id: c.id, name: c.name, country_name: c.countryName })),
      byCompetition: { [String(compId)]: comp },
    };
    return {
      spd,
      state: { compId: String(compId), comp, seasonCodes, seasonCode, season: comp.seasons[seasonCode]! },
      ctx: { matchResults: season.matchResults, matchGoals: season.matchGoals, playersById: season.playersById },
    };
  }, [comps, compId, seasonCodes, seasonCode, season, playerId]);
}
